import React, { useCallback, useEffect } from 'react';
import { GetStaticPaths, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';

import Survey from 'qualification/components/Survey';
import SurveyNavbar from 'qualification/components/Survey/SurveyNavbar';
import {
  useQualification,
  withQualificationProvider,
} from 'qualification/context';
import { QUALIFICATION_STATUS, StepState } from 'qualification/context/model';
import { isMainQuestionId } from 'qualification/schema';
import { SCHEMA_STEPS, steps } from 'qualification/schema/steps';
import { INDEX_LOCALES } from 'shared/constants';
import { getQueryParams } from 'shared/helpers/urls';
import { PageLayout } from 'shared/layouts/MainLayout';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';

const ENTRY_POINT = '/start';

type Props = InferGetStaticPropsType<typeof getStaticProps>;

function Step({ ratings }: Props) {
  const router = useRouter();

  const { state, actions } = useQualification({
    onQuestionChange: useCallback(
      (step?: StepState | null) => {
        // Change the route, when the question changes
        const updateRouteOnQuestionChange = () => {
          if (!step) {
            router.push({ pathname: ENTRY_POINT, query: getQueryParams() });
          }
          if (step) {
            if (!router.pathname.includes(step.id)) {
              router.push(
                {
                  pathname: step.id,
                  query: getQueryParams(),
                },
                undefined,
                { shallow: true }
              );
            }
          }
        };
        updateRouteOnQuestionChange();
      },
      [router?.replace]
    ),
    onAllQuestionAnswered: useCallback(() => {
      router.replace('finding-caterers');
    }, [router]),
  });

  // Route hooks, to syc with state, and validate the routes
  useEffect(() => {
    const updateQuestionOnRouteChange = (url: string) => {
      const pathnameParts = url.split('/');
      const questionId = pathnameParts[pathnameParts.length - 1];
      if (isMainQuestionId(questionId)) {
        actions.setQuestion(questionId);
      }
    };

    router.events.on('routeChangeStart', updateQuestionOnRouteChange);

    return () => {
      router.events.off('routeChangeStart', updateQuestionOnRouteChange);
    };
  }, [router, state]);

  useEffect(() => {
    if (state.status === QUALIFICATION_STATUS.qualifiedForRequest) {
      actions.prevStep(); // Move back into 'answering' status
      return;
    }
    if (state.status === QUALIFICATION_STATUS.initial) {
      router.replace(ENTRY_POINT);
    }

    router.prefetch('/start/finding-caterers');
  }, [state.status, router]);

  if (state.status !== QUALIFICATION_STATUS.answering) {
    return null;
  }

  return (
    <PageLayout
      main={
        state.qualification.step && (
          <>
            <SurveyNavbar ratings={ratings} />
            <Survey
              step={SCHEMA_STEPS[state.qualification.step.id]}
              caterer={state.qualification.selectedCaterer}
            />
          </>
        )
      }
    />
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { stepId: string }; locale: string }[] = [];
  const stepIds = Object.values(steps).map(({ id }) => id);

  INDEX_LOCALES.forEach((locale) => {
    paths.push(
      ...stepIds.map((id) => ({
        params: {
          stepId: id,
        },
        locale,
      }))
    );
  });

  return {
    paths: paths,
    fallback: false,
  };
};

export async function getStaticProps() {
  const ratings = await fetchRatings();
  return {
    props: {
      robots: 'none',
      ratings,
    },
  };
}

export default withQualificationProvider(
  { storageKey: '__funnel', storage: 'sessionStorage' },
  Step
);
